// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { defer } from 'lodash/fp';

// Utils
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import { getGestureCentre, getGestureSize } from '../../utils/dom/touchEventUtil';
import { getZoomDirection, getZoomNextScaleOnTouch, ZOOM_DIRECTIONS } from './zoomTouchUtils';

/**
 * Attaches the touch events to handle zoom on touch devices.
 */
class ZoomTouchHandler extends React.Component {
    componentDidMount() {
        defer(() => {
            const { canvasViewportRef } = this.props;
            canvasViewportRef.current.addEventListener('touchstart', this.onTouchStart);
        });
    }
    /**
     * Add touch handlers to check for zoom.
     */
    onTouchStart = (e) => {
        document.addEventListener('touchmove', this.startTouchZoom);
        document.addEventListener('touchend', this.removeTouchEventListeners);
    };

    /**
     * If we have what looks like a zoom operation beginning, record the initial state and
     * add new event listeners to track the changes.
     */
    startTouchZoom = (e) => {
        const { getZoomCanvasTransformationMatrix, dispatchFinishEditingCurrentElement } = this.props;

        if (e.touches.length !== 2) return;

        this.removeTouchEventListeners();

        dispatchFinishEditingCurrentElement();

        const transformationMatrix = getZoomCanvasTransformationMatrix();

        this.gestureSizeStart = getGestureSize(e);
        this.gestureScaleStart = matrixLib.getScale(transformationMatrix);
        this.zoomDirection = ZOOM_DIRECTIONS.UNKNOWN;

        document.addEventListener('touchmove', this.onTouchMove, { passive: false });
        document.addEventListener('touchend', this.onTouchEnd, { passive: false });
    };

    /**
     * For touch changes during a zoom operation, determine the new zoom and then render it.
     */
    onTouchMove = (e) => {
        if (e.touches.length !== 2) return;

        e.preventDefault();
        e.stopPropagation();

        const { canvasDocumentSize, immediatelyScaleTo, domElementPropertyCache } = this.props;

        const { canvasViewportClientRect } = domElementPropertyCache.getCachedValues(
            this.props.canvasViewportRef.current,
        );

        const gestureSize = getGestureSize(e);
        const eventPosition = getGestureCentre(e);

        const ratioChange = gestureSize / this.gestureSizeStart;
        this.zoomDirection = getZoomDirection(ratioChange, this.zoomDirection);

        const nextScale = getZoomNextScaleOnTouch(
            canvasViewportClientRect,
            canvasDocumentSize,
            this.gestureScaleStart * ratioChange,
            this.gestureScaleStart,
            this.zoomDirection,
        );

        immediatelyScaleTo(nextScale, { eventPosition });
    };

    onTouchEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();

        document.removeEventListener('touchmove', this.onTouchMove, { passive: false });
        document.removeEventListener('touchend', this.onTouchEnd, { passive: false });
    };

    removeTouchEventListeners = () => {
        document.removeEventListener('touchmove', this.startTouchZoom);
        document.removeEventListener('touchmove', this.onTouchMove, { passive: false });
        document.removeEventListener('touchend', this.onTouchEnd, { passive: false });
    };

    render() {
        return null;
    }
}

ZoomTouchHandler.propTypes = {
    canvasViewportRef: PropTypes.object,
    canvasDocumentSize: PropTypes.object,

    getZoomCanvasTransformationMatrix: PropTypes.func,
    domElementPropertyCache: PropTypes.object,

    immediatelyScaleTo: PropTypes.func,
    dispatchFinishEditingCurrentElement: PropTypes.func,
};

export default ZoomTouchHandler;
